{
  const TOUCH_AXIS_DETECTION_DELTA_THRESHOLD = 8
  const SWIPE_ADVANCE_PREVIEW_WIDTH_DIVISOR = 4
  const SWIPE_ADVANCE_MAX_THRESHOLD = 120
  const FLICK_DISTANCE_THRESHOLD = 30
  const FLICK_VELOCITY_THRESHOLD = 0.45
  const MIN_TOUCH_DURATION_MS = 1
  const TAP_SUPPRESSION_DURATION_MS = 350
  const INTERACTIVE_VIEWER_TARGET_SELECTOR =
    'a, button, input, select, textarea, label, summary, [data-stories-close], [data-slide-prev], [data-slide-next], [data-story-set-prev], [data-story-set-next], [data-story-sound-toggle]'

  class StoriesContent {
    constructor(root) {
      this.root = root
      this.viewer = root.querySelector('[data-stories-viewer]')
      this.storyTriggers = [...root.querySelectorAll('[data-story-trigger]')]
      this.storyNodes = this.viewer ? [...this.viewer.querySelectorAll('.stories-block__viewer-story')] : []
      this.closeTriggers = this.viewer ? [...this.viewer.querySelectorAll('[data-stories-close]')] : []
      this.prevStorySetButtons = this.viewer ? [...this.viewer.querySelectorAll('[data-story-set-prev]')] : []
      this.nextStorySetButtons = this.viewer ? [...this.viewer.querySelectorAll('[data-story-set-next]')] : []
      this.nextSlideButton = this.viewer ? this.viewer.querySelector('[data-slide-next]') : null
      this.prevSlideButton = this.viewer ? this.viewer.querySelector('[data-slide-prev]') : null
      this.closeButton = this.viewer ? this.viewer.querySelector('.stories-block__viewer-close') : null
      this.soundToggleButton = this.viewer ? this.viewer.querySelector('[data-story-sound-toggle]') : null
      this.viewerDialog = this.viewer ? this.viewer.querySelector('.stories-block__viewer-dialog') : null
      this.mobileViewportQuery = window.matchMedia('(max-width: 767px)')

      this.activeStoryIndex = 0
      this.activeSlideIndex = 0
      this.wasMobileViewport = this.mobileViewportQuery.matches
      this.lastTrigger = null
      this.touchStartX = 0
      this.touchStartY = 0
      this.touchStartTime = 0
      this.touchDeltaX = 0
      this.touchDeltaY = 0
      this.touchGestureAxis = null
      this.isTouchTracking = false
      this.dragSettleTimeout = null
      this.dragTransitionDuration = 220
      this.isDragSettling = false
      this.storyTransitionTimeout = null
      this.storyTransitionDuration = 320
      this.storyTransition = null
      this.lastActiveVideo = null
      this.isSoundEnabled = true
      this.tapSuppressedUntil = 0

      this.handleKeydown = this.handleKeydown.bind(this)
      this.handleTouchStart = this.handleTouchStart.bind(this)
      this.handleTouchMove = this.handleTouchMove.bind(this)
      this.handleTouchEnd = this.handleTouchEnd.bind(this)
      this.handleViewerClick = this.handleViewerClick.bind(this)
      this.handleViewportChange = this.handleViewportChange.bind(this)
      this.handleActiveVideoMetadata = this.handleActiveVideoMetadata.bind(this)

      if (!this.viewer || !this.storyNodes.length || !this.storyTriggers.length) {
        return
      }

      this.initializeNavigationButtons()
      this.initializeSoundToggle()

      this.mountViewer()

      this.bindEvents()
    }

    initializeNavigationButtons() {
      ;[
        this.prevSlideButton,
        this.nextSlideButton,
        ...this.prevStorySetButtons,
        ...this.nextStorySetButtons,
      ].forEach((button) => {
        if (!button) {
          return
        }

        button.hidden = false
        button.disabled = true
        button.tabIndex = -1
        button.setAttribute('data-button-visible', 'false')
        button.setAttribute('aria-hidden', 'true')
      })
    }

    initializeSoundToggle() {
      if (!this.soundToggleButton) {
        return
      }

      this.soundToggleButton.hidden = true
      this.soundToggleButton.setAttribute('data-sound-enabled', 'true')
      this.soundToggleButton.setAttribute('aria-pressed', 'true')
      this.soundToggleButton.setAttribute('aria-hidden', 'true')
    }

    setButtonState(button, isVisible) {
      if (!button) {
        return
      }

      button.hidden = false
      button.disabled = !isVisible
      button.setAttribute('data-button-visible', isVisible ? 'true' : 'false')
      button.setAttribute('aria-hidden', isVisible ? 'false' : 'true')

      if (isVisible) {
        button.removeAttribute('tabindex')
        return
      }

      button.tabIndex = -1
    }

    setSoundToggleState(isVisible) {
      if (!this.soundToggleButton) {
        return
      }

      this.soundToggleButton.hidden = !isVisible
      this.soundToggleButton.setAttribute('aria-hidden', isVisible ? 'false' : 'true')
      this.soundToggleButton.setAttribute('aria-pressed', this.isSoundEnabled ? 'true' : 'false')
      this.soundToggleButton.setAttribute('data-sound-enabled', this.isSoundEnabled ? 'true' : 'false')
      this.soundToggleButton.setAttribute('aria-label', this.isSoundEnabled ? 'Turn sound off' : 'Turn sound on')

      if (isVisible) {
        this.soundToggleButton.removeAttribute('tabindex')
        return
      }

      this.soundToggleButton.tabIndex = -1
    }

    getActiveSlide() {
      const activeStory = this.storyNodes[this.activeStoryIndex]

      if (!activeStory) {
        return null
      }

      return activeStory.querySelector(`.stories-block__viewer-slide[data-slide-index="${this.activeSlideIndex}"]`)
    }

    isMobileViewport() {
      return this.mobileViewportQuery.matches
    }

    getSlidesForStory(story = this.storyNodes[this.activeStoryIndex]) {
      if (!story) {
        return []
      }

      return [...story.querySelectorAll('.stories-block__viewer-slide')]
    }

    getSlide(storyIndex, slideIndex) {
      const story = this.storyNodes[storyIndex]

      if (!story) {
        return null
      }

      return story.querySelector(`.stories-block__viewer-slide[data-slide-index="${slideIndex}"]`)
    }

    isVideoVisible(video) {
      if (!video || video.hidden) {
        return false
      }

      const styles = window.getComputedStyle(video)

      return styles.display !== 'none' && styles.visibility !== 'hidden'
    }

    getSlideVideo(slide) {
      if (!slide) {
        return null
      }

      const videos = [...slide.querySelectorAll('[data-story-video], video')]

      if (!videos.length) {
        return null
      }

      return videos.find((video) => this.isVideoVisible(video)) || videos[0]
    }

    videoHasAudio(video) {
      if (!video) {
        return false
      }

      if (typeof video.mozHasAudio === 'boolean') {
        return video.mozHasAudio
      }

      if (video.audioTracks && typeof video.audioTracks.length === 'number') {
        return video.audioTracks.length > 0
      }

      if (typeof video.webkitAudioDecodedByteCount === 'number' && video.readyState >= 1) {
        return video.webkitAudioDecodedByteCount > 0
      }

      return false
    }

    watchVideoMetadata(video) {
      if (!video || video.readyState >= 1) {
        return
      }

      video.addEventListener('loadedmetadata', this.handleActiveVideoMetadata, { once: true })
      video.addEventListener('canplay', this.handleActiveVideoMetadata, { once: true })
    }

    syncSoundToggleForActiveVideo() {
      const activeVideo = this.getSlideVideo(this.getActiveSlide())
      const hasAudio = this.videoHasAudio(activeVideo)

      this.setSoundToggleState(hasAudio)
      this.watchVideoMetadata(activeVideo)

      return hasAudio
    }

    handleActiveVideoMetadata() {
      if (this.viewer.hidden) {
        return
      }

      this.syncSoundToggleForActiveVideo()
    }

    applyVideoSoundState(video) {
      if (!video) {
        return
      }

      video.muted = !this.isSoundEnabled

      if (this.isSoundEnabled) {
        video.removeAttribute('muted')
      } else {
        video.setAttribute('muted', 'muted')
      }
    }

    toggleSound() {
      const activeVideo = this.getSlideVideo(this.getActiveSlide())

      if (!activeVideo || !this.videoHasAudio(activeVideo)) {
        if (this.isSoundEnabled) {
          this.isSoundEnabled = false
        }

        this.setSoundToggleState(false)
        return
      }

      this.isSoundEnabled = !this.isSoundEnabled
      this.applyVideoSoundState(activeVideo)
      this.setSoundToggleState(true)

      if (this.isSoundEnabled) {
        const playPromise = activeVideo.play()

        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {})
        }
      }
    }

    getStepTarget(direction) {
      const slides = this.getSlidesForStory()

      if (direction === 'next') {
        if (this.activeSlideIndex < slides.length - 1) {
          return {
            storyIndex: this.activeStoryIndex,
            slideIndex: this.activeSlideIndex + 1,
          }
        }

        if (this.activeStoryIndex < this.storyNodes.length - 1) {
          return {
            storyIndex: this.activeStoryIndex + 1,
            slideIndex: 0,
          }
        }

        return null
      }

      if (this.activeSlideIndex > 0) {
        return {
          storyIndex: this.activeStoryIndex,
          slideIndex: this.activeSlideIndex - 1,
        }
      }

      if (this.activeStoryIndex <= 0) {
        return null
      }

      const prevStoryIndex = this.activeStoryIndex - 1
      const prevStorySlides = this.getSlidesForStory(this.storyNodes[prevStoryIndex])

      return {
        storyIndex: prevStoryIndex,
        slideIndex: Math.max(prevStorySlides.length - 1, 0),
      }
    }

    clearDragPreview() {
      if (this.viewerDialog) {
        this.viewerDialog.removeAttribute('data-dragging')
      }

      this.storyNodes.forEach((story) => {
        story.removeAttribute('data-drag-preview')
        story.style.removeProperty('--stories-preview-z-index')
        story.style.removeProperty('--stories-story-offset')

        const slides = this.getSlidesForStory(story)

        slides.forEach((slide) => {
          slide.removeAttribute('data-drag-preview')
          slide.style.removeProperty('--stories-slide-offset')
        })
      })
    }

    stopDragSettleAnimation() {
      if (this.dragSettleTimeout) {
        window.clearTimeout(this.dragSettleTimeout)
        this.dragSettleTimeout = null
      }

      this.isDragSettling = false
    }

    stopStoryTransition() {
      if (this.storyTransitionTimeout) {
        window.clearTimeout(this.storyTransitionTimeout)
        this.storyTransitionTimeout = null
      }

      if (!this.storyTransition) {
        return
      }

      const { fromStoryIndex, toStoryIndex } = this.storyTransition

      ;[fromStoryIndex, toStoryIndex].forEach((storyIndex) => {
        const story = this.storyNodes[storyIndex]

        if (!story) {
          return
        }

        story.removeAttribute('data-story-transition')
        story.removeAttribute('data-story-transition-direction')
      })

      this.storyTransition = null
    }

    transitionStorySet(targetStoryIndex) {
      if (
        !this.isMobileViewport() &&
        targetStoryIndex >= 0 &&
        targetStoryIndex < this.storyNodes.length &&
        targetStoryIndex !== this.activeStoryIndex
      ) {
        const fromStoryIndex = this.activeStoryIndex
        const direction = targetStoryIndex > fromStoryIndex ? 'next' : 'prev'

        this.stopStoryTransition()
        this.pauseActiveVideo()

        this.storyTransition = {
          fromStoryIndex,
          toStoryIndex: targetStoryIndex,
          direction,
        }

        this.activeStoryIndex = targetStoryIndex
        this.activeSlideIndex = 0
        this.render()

        const activeTransition = this.storyTransition
        const fromStory = this.storyNodes[fromStoryIndex]
        const toStory = this.storyNodes[targetStoryIndex]

        if (!activeTransition || !fromStory || !toStory) {
          this.stopStoryTransition()
          this.render()
          return
        }

        window.requestAnimationFrame(() => {
          if (this.storyTransition !== activeTransition) {
            return
          }

          fromStory.setAttribute('data-story-transition', 'from-end')
          toStory.setAttribute('data-story-transition', 'to-end')
        })

        this.storyTransitionTimeout = window.setTimeout(() => {
          if (this.storyTransition !== activeTransition) {
            return
          }

          this.stopStoryTransition()
          this.render()
        }, this.storyTransitionDuration)

        return
      }

      this.activeStoryIndex = targetStoryIndex
      this.activeSlideIndex = 0
      this.render()
    }

    setStepPreview(direction, target, previewOffset) {
      const activeStory = this.storyNodes[this.activeStoryIndex]
      const activeSlide = this.getActiveSlide()

      if (!activeStory || !activeSlide) {
        return false
      }

      this.clearDragPreview()

      const previewWidth = this.viewerDialog ? this.viewerDialog.clientWidth || window.innerWidth : window.innerWidth
      const isCrossStoryStep = target && target.storyIndex !== this.activeStoryIndex

      if (this.viewerDialog) {
        this.viewerDialog.setAttribute('data-dragging', 'true')
      }

      activeStory.hidden = false
      activeStory.setAttribute('data-drag-preview', 'active')
      activeStory.style.setProperty('--stories-preview-z-index', '2')
      activeSlide.hidden = false
      activeSlide.setAttribute('data-drag-preview', 'active')

      if (isCrossStoryStep) {
        activeStory.style.setProperty('--stories-story-offset', `${previewOffset}px`)
        activeSlide.style.removeProperty('--stories-slide-offset')
      } else {
        activeSlide.style.setProperty('--stories-slide-offset', `${previewOffset}px`)
      }

      if (!target) {
        return true
      }

      const targetStory = this.storyNodes[target.storyIndex]
      const targetSlide = this.getSlide(target.storyIndex, target.slideIndex)

      if (!targetStory || !targetSlide) {
        return true
      }

      targetStory.hidden = false
      targetStory.setAttribute('data-drag-preview', 'target')
      targetStory.style.setProperty('--stories-preview-z-index', '1')
      targetSlide.hidden = false
      targetSlide.setAttribute('data-drag-preview', 'target')

      if (isCrossStoryStep) {
        targetStory.style.setProperty(
          '--stories-story-offset',
          `${previewOffset + (direction === 'next' ? previewWidth : -previewWidth)}px`
        )
        targetSlide.style.removeProperty('--stories-slide-offset')
      } else {
        targetSlide.style.setProperty(
          '--stories-slide-offset',
          `${previewOffset + (direction === 'next' ? previewWidth : -previewWidth)}px`
        )
      }

      return true
    }

    applyDragPreview() {
      if (!this.isMobileViewport()) {
        return
      }

      const direction = this.touchDeltaX < 0 ? 'next' : 'prev'
      const target = this.getStepTarget(direction)
      const previewOffset = target ? this.touchDeltaX : this.touchDeltaX * 0.35

      this.setStepPreview(direction, target, previewOffset)
    }

    triggerTapStep(direction) {
      if (!this.isMobileViewport() || this.isDragSettling || this.viewer.hidden) {
        return
      }

      const target = this.getStepTarget(direction)

      if (!target) {
        return
      }

      this.pauseActiveVideo()

      if (!this.setStepPreview(direction, target, 0)) {
        return
      }

      this.animateDragRelease({
        shouldAdvance: true,
        direction,
        target,
      })
    }

    animateDragRelease({ shouldAdvance, direction, target }) {
      const activeStory = this.storyNodes[this.activeStoryIndex]
      const activeSlide = this.getActiveSlide()
      const previewWidth = this.viewerDialog ? this.viewerDialog.clientWidth || window.innerWidth : window.innerWidth
      const targetStory = target ? this.storyNodes[target.storyIndex] : null
      const targetSlide = target ? this.getSlide(target.storyIndex, target.slideIndex) : null
      const isCrossStoryStep = target && target.storyIndex !== this.activeStoryIndex

      if (!activeStory || !activeSlide) {
        this.resetTouchState()
        this.render()
        return
      }

      this.stopDragSettleAnimation()
      this.isDragSettling = true

      if (this.viewerDialog) {
        this.viewerDialog.removeAttribute('data-dragging')
      }

      window.requestAnimationFrame(() => {
        activeStory.hidden = false
        activeSlide.hidden = false

        if (isCrossStoryStep) {
          activeStory.style.setProperty(
            '--stories-story-offset',
            shouldAdvance ? `${direction === 'next' ? -previewWidth : previewWidth}px` : '0px'
          )
          activeSlide.style.removeProperty('--stories-slide-offset')
        } else {
          activeSlide.style.setProperty(
            '--stories-slide-offset',
            shouldAdvance ? `${direction === 'next' ? -previewWidth : previewWidth}px` : '0px'
          )
        }

        if (targetStory && targetSlide) {
          targetStory.hidden = false
          targetSlide.hidden = false

          if (isCrossStoryStep) {
            targetStory.style.setProperty(
              '--stories-story-offset',
              shouldAdvance ? '0px' : `${direction === 'next' ? previewWidth : -previewWidth}px`
            )
            targetSlide.style.removeProperty('--stories-slide-offset')
          } else {
            targetSlide.style.setProperty(
              '--stories-slide-offset',
              shouldAdvance ? '0px' : `${direction === 'next' ? previewWidth : -previewWidth}px`
            )
          }
        }
      })

      this.dragSettleTimeout = window.setTimeout(() => {
        if (shouldAdvance && target) {
          this.activeStoryIndex = target.storyIndex
          this.activeSlideIndex = target.slideIndex
        }

        this.dragSettleTimeout = null
        this.isDragSettling = false
        this.resetTouchState()
        this.render()
      }, this.dragTransitionDuration)
    }

    mountViewer() {
      if (!this.viewer || this.viewer.parentNode === document.body) {
        return
      }

      document.body.appendChild(this.viewer)
    }

    bindEvents() {
      this.storyTriggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const storyIndex = Number(trigger.dataset.storyIndex)
          this.open(storyIndex, trigger)
        })
      })

      this.closeTriggers.forEach((trigger) => {
        trigger.addEventListener('click', () => this.close())
      })

      if (this.nextSlideButton) {
          this.nextSlideButton.addEventListener('click', () => this.goToNextStep())
      }

      if (this.soundToggleButton) {
        this.soundToggleButton.addEventListener('click', () => this.toggleSound())
      }

      this.prevStorySetButtons.forEach((button) => {
        button.addEventListener('click', () => this.prevStory())
      })

      this.nextStorySetButtons.forEach((button) => {
        button.addEventListener('click', () => this.nextStory())
      })

      if (this.prevSlideButton) {
        this.prevSlideButton.addEventListener('click', () => this.goToPrevStep())
      }

      if (this.viewerDialog) {
        this.viewerDialog.addEventListener('touchstart', this.handleTouchStart, { passive: true })
        this.viewerDialog.addEventListener('touchmove', this.handleTouchMove, { passive: true })
        this.viewerDialog.addEventListener('touchend', this.handleTouchEnd)
        this.viewerDialog.addEventListener('click', this.handleViewerClick)
      }

      if (typeof this.mobileViewportQuery.addEventListener === 'function') {
        this.mobileViewportQuery.addEventListener('change', this.handleViewportChange)
      } else if (typeof this.mobileViewportQuery.addListener === 'function') {
        this.mobileViewportQuery.addListener(this.handleViewportChange)
      }
    }

    handleViewportChange(event) {
      const isMobileViewport = event.matches

      if (this.wasMobileViewport && !isMobileViewport && !this.viewer.hidden) {
        this.close()
      }

      this.wasMobileViewport = isMobileViewport
    }

    open(storyIndex, trigger) {
      this.stopDragSettleAnimation()
      this.stopStoryTransition()
      this.lastTrigger = trigger
      this.activeStoryIndex = Number.isNaN(storyIndex) ? 0 : storyIndex
      this.activeSlideIndex = 0
      this.isSoundEnabled = true

      this.viewer.hidden = false
      this.viewer.setAttribute('aria-hidden', 'false')
      document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', this.handleKeydown)

      this.render()

      if (this.closeButton) {
        this.closeButton.focus()
      }

      this.resetTouchState()
    }

    close() {
      this.stopDragSettleAnimation()
      this.stopStoryTransition()
      this.pauseAllVideos()
      this.isSoundEnabled = true
      this.viewer.hidden = true
      this.viewer.setAttribute('aria-hidden', 'true')
      document.body.style.overflow = ''
      document.removeEventListener('keydown', this.handleKeydown)
      this.resetTouchState()

      if (this.lastTrigger) {
        this.lastTrigger.focus()
      }
    }

    nextSlide() {
      const slides = this.getSlidesForStory()

      if (this.activeSlideIndex < slides.length - 1) {
        this.activeSlideIndex += 1
        this.render()
      }
    }

    prevSlide() {
      const story = this.storyNodes[this.activeStoryIndex]

      if (!story) {
        return
      }

      if (this.activeSlideIndex > 0) {
        this.activeSlideIndex -= 1
        this.render()
      }
    }

    nextStory() {
      if (this.storyNodes.length < 2 || this.activeStoryIndex >= this.storyNodes.length - 1) {
        return
      }

      this.transitionStorySet(this.activeStoryIndex + 1)
    }

    prevStory() {
      if (this.storyNodes.length < 2 || this.activeStoryIndex <= 0) {
        return
      }

      this.transitionStorySet(this.activeStoryIndex - 1)
    }

    goToNextStep() {
      const slides = this.getSlidesForStory()

      if (this.activeSlideIndex < slides.length - 1) {
        this.activeSlideIndex += 1
        this.render()
        return
      }

      if (this.activeStoryIndex >= this.storyNodes.length - 1) {
        return
      }

      this.transitionStorySet(this.activeStoryIndex + 1)
    }

    goToPrevStep() {
      if (this.activeSlideIndex > 0) {
        this.activeSlideIndex -= 1
        this.render()
        return
      }

      if (this.activeStoryIndex <= 0) {
        return
      }

      if (!this.isMobileViewport()) {
        this.transitionStorySet(this.activeStoryIndex - 1)
        return
      }

      this.activeStoryIndex -= 1

      const slides = this.getSlidesForStory()
      this.activeSlideIndex = Math.max(slides.length - 1, 0)
      this.render()
    }

    resetTouchState() {
      this.clearDragPreview()
      this.touchStartX = 0
      this.touchStartY = 0
      this.touchStartTime = 0
      this.touchDeltaX = 0
      this.touchDeltaY = 0
      this.touchGestureAxis = null
      this.isTouchTracking = false
    }

    handleTouchStart(event) {
      if (this.isDragSettling) {
        return
      }

      if (!this.isMobileViewport() || event.touches.length !== 1) {
        this.resetTouchState()
        return
      }

      this.clearDragPreview()
      this.pauseActiveVideo()

      const [{ clientX, clientY }] = event.touches

      this.touchStartX = clientX
      this.touchStartY = clientY
      this.touchStartTime = typeof event.timeStamp === 'number' ? event.timeStamp : performance.now()
      this.touchDeltaX = 0
      this.touchDeltaY = 0
      this.touchGestureAxis = null
      this.isTouchTracking = true
    }

    handleTouchMove(event) {
      if (this.isDragSettling || !this.isTouchTracking || event.touches.length !== 1) {
        return
      }

      const [{ clientX, clientY }] = event.touches

      this.touchDeltaX = clientX - this.touchStartX
      this.touchDeltaY = clientY - this.touchStartY

      if (!this.touchGestureAxis) {
        const hasMeaningfulHorizontalDelta =
          Math.abs(this.touchDeltaX) > TOUCH_AXIS_DETECTION_DELTA_THRESHOLD &&
          Math.abs(this.touchDeltaX) >= Math.abs(this.touchDeltaY)
        const hasMeaningfulVerticalDelta =
          Math.abs(this.touchDeltaY) > TOUCH_AXIS_DETECTION_DELTA_THRESHOLD &&
          Math.abs(this.touchDeltaY) > Math.abs(this.touchDeltaX)

        if (hasMeaningfulHorizontalDelta) {
          this.touchGestureAxis = 'horizontal'
        } else if (hasMeaningfulVerticalDelta) {
          this.touchGestureAxis = 'vertical'
        }
      }

      if (this.touchGestureAxis !== 'horizontal') {
        return
      }

      this.applyDragPreview()
    }

    handleTouchEnd(event) {
      if (this.isDragSettling || !this.isTouchTracking) {
        return
      }

      const previewWidth = this.viewerDialog ? this.viewerDialog.clientWidth || window.innerWidth : window.innerWidth
      const swipeAdvanceThreshold = Math.min(previewWidth / SWIPE_ADVANCE_PREVIEW_WIDTH_DIVISOR, SWIPE_ADVANCE_MAX_THRESHOLD)
      const touchEndTime = typeof event.timeStamp === 'number' ? event.timeStamp : performance.now()
      const touchDuration = Math.max(touchEndTime - this.touchStartTime, MIN_TOUCH_DURATION_MS)
      const swipeVelocity = Math.abs(this.touchDeltaX) / touchDuration
      const swipeDirection = this.touchDeltaX < 0 ? 'next' : 'prev'
      const targetStep = this.getStepTarget(swipeDirection)
      const hasTargetStep = Boolean(targetStep)
      const hasDistanceSwipe = Math.abs(this.touchDeltaX) >= swipeAdvanceThreshold
      const hasFlickSwipe = Math.abs(this.touchDeltaX) >= FLICK_DISTANCE_THRESHOLD && swipeVelocity >= FLICK_VELOCITY_THRESHOLD
      const shouldAdvance =
        this.touchGestureAxis === 'horizontal' &&
        hasTargetStep &&
        (hasDistanceSwipe || hasFlickSwipe)

      if (this.touchGestureAxis === 'horizontal') {
        this.tapSuppressedUntil = Date.now() + TAP_SUPPRESSION_DURATION_MS
      }

      if (this.touchGestureAxis === 'horizontal') {
        this.animateDragRelease({
          shouldAdvance,
          direction: swipeDirection,
          target: targetStep,
        })
      } else {
        this.resetTouchState()
        this.render()
      }
    }

    handleViewerClick(event) {
      if (!this.isMobileViewport() || this.viewer.hidden || this.isDragSettling) {
        return
      }

      if (Date.now() < this.tapSuppressedUntil) {
        return
      }

      if (
        event.target instanceof Element &&
        event.target.closest(INTERACTIVE_VIEWER_TARGET_SELECTOR)
      ) {
        return
      }

      const dialogBounds = this.viewerDialog ? this.viewerDialog.getBoundingClientRect() : null

      if (!dialogBounds || dialogBounds.width <= 0) {
        return
      }

      const offsetX = event.clientX - dialogBounds.left
      const direction = offsetX < dialogBounds.width / 2 ? 'prev' : 'next'

      event.preventDefault()
      this.triggerTapStep(direction)
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.close()
        return
      }

      if (event.key === 'ArrowRight') {
        this.goToNextStep()
      }

      if (event.key === 'ArrowLeft') {
        this.goToPrevStep()
      }
    }

    render() {
      const isMobileViewport = this.isMobileViewport()
      const storyTransition = !isMobileViewport ? this.storyTransition : null

      this.clearDragPreview()
      this.setButtonState(this.prevSlideButton, false)
      this.setButtonState(this.nextSlideButton, false)
      this.setSoundToggleState(false)

      this.prevStorySetButtons.forEach((button) => this.setButtonState(button, false))
      this.nextStorySetButtons.forEach((button) => this.setButtonState(button, false))

      this.storyNodes.forEach((story, index) => {
        const isActiveStory = index === this.activeStoryIndex
        const isLeavingStory = storyTransition ? index === storyTransition.fromStoryIndex : false
        const prevStorySetButton = story.querySelector('[data-story-set-prev]')
        const nextStorySetButton = story.querySelector('[data-story-set-next]')

        story.hidden = !isActiveStory && !isLeavingStory

        if (storyTransition && (isActiveStory || isLeavingStory)) {
          story.setAttribute('data-story-transition-direction', storyTransition.direction)

          if (!story.hasAttribute('data-story-transition')) {
            story.setAttribute('data-story-transition', isLeavingStory ? 'from-start' : 'to-start')
          }
        } else {
          story.removeAttribute('data-story-transition')
          story.removeAttribute('data-story-transition-direction')
        }

        const slides = this.getSlidesForStory(story)
        const progressLines = [...story.querySelectorAll('[data-story-progress-index]')]

        if (isLeavingStory) {
          return
        }

        if (!isActiveStory) {
          slides.forEach((slide) => {
            slide.hidden = true
            slide.style.gridColumn = ''
          })

          progressLines.forEach((line) => {
            line.removeAttribute('data-active-progress')
          })

          return
        }

        if (this.activeSlideIndex > slides.length - 1) {
          this.activeSlideIndex = slides.length - 1
        }

        slides.forEach((slide, slideIndex) => {
          const desktopOffset = slideIndex - this.activeSlideIndex

          slide.hidden = isMobileViewport ? slideIndex !== this.activeSlideIndex : false
          slide.style.gridColumn = ''
          slide.style.removeProperty('--stories-slide-offset')
          slide.removeAttribute('data-desktop-visible')
          slide.removeAttribute('data-desktop-position')
          slide.removeAttribute('data-active-slide')

          if (!isMobileViewport) {
            if (desktopOffset < -1) {
              slide.setAttribute('data-desktop-position', 'far-left')
            } else if (desktopOffset === -1) {
              slide.setAttribute('data-desktop-position', 'left')
              slide.setAttribute('data-desktop-visible', 'true')
            } else if (desktopOffset === 0) {
              slide.setAttribute('data-desktop-position', 'center')
              slide.setAttribute('data-desktop-visible', 'true')
            } else if (desktopOffset === 1) {
              slide.setAttribute('data-desktop-position', 'right')
              slide.setAttribute('data-desktop-visible', 'true')
            } else {
              slide.setAttribute('data-desktop-position', 'far-right')
            }
          }

          if (!isMobileViewport && Math.abs(desktopOffset) <= 1) {
            slide.setAttribute('data-desktop-visible', 'true')
          }
        })

        progressLines.forEach((line, progressIndex) => {
          if (progressIndex === this.activeSlideIndex) {
            line.setAttribute('data-active-progress', 'true')
            return
          }

          line.removeAttribute('data-active-progress')
        })

        const activeSlide = this.getActiveSlide()

        if (!activeSlide) {
          return
        }

        activeSlide.setAttribute('data-active-slide', 'true')
        this.syncSoundToggleForActiveVideo()

        if (prevStorySetButton) {
          const isFirstSlide = this.activeSlideIndex <= 0
          const isFirstStorySet = this.activeStoryIndex <= 0

          this.setButtonState(prevStorySetButton, isFirstSlide && !isFirstStorySet)
        }

        if (nextStorySetButton) {
          const isLastSlide = this.activeSlideIndex >= slides.length - 1
          const isLastStorySet = this.activeStoryIndex >= this.storyNodes.length - 1

          this.setButtonState(nextStorySetButton, isLastSlide && !isLastStorySet)
        }

        if (this.nextSlideButton) {
          this.setButtonState(this.nextSlideButton, this.activeSlideIndex < slides.length - 1)
        }

        if (this.prevSlideButton) {
          this.setButtonState(this.prevSlideButton, this.activeSlideIndex > 0)
        }

        if (isMobileViewport) {
          return
        }
      })

      this.syncVideos()
    }

    syncVideos() {
      const activeStory = this.storyNodes[this.activeStoryIndex]

      if (!activeStory) {
        this.lastActiveVideo = null
        return
      }

      const activeSlide = this.getActiveSlide()

      if (!activeSlide) {
        this.lastActiveVideo = null
        return
      }

      const activeVideo = this.getSlideVideo(activeSlide)

      if (!activeVideo) {
        this.pauseAllVideos({ reset: false })
        this.lastActiveVideo = null
        return
      }

      this.pauseAllVideos({ except: activeVideo, reset: false })
      this.applyVideoSoundState(activeVideo)

      if (this.lastActiveVideo !== activeVideo) {
        activeVideo.currentTime = 0
      }

      this.lastActiveVideo = activeVideo
      const playPromise = activeVideo.play()

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {})
      }
    }

    pauseActiveVideo() {
      if (!this.lastActiveVideo) {
        return
      }

      this.lastActiveVideo.pause()
    }

    pauseAllVideos({ except = null, reset = true } = {}) {
      const videos = this.viewer.querySelectorAll('[data-story-video], video')

      videos.forEach((video) => {
        if (video === except) {
          return
        }

        video.pause()

        if (reset) {
          video.currentTime = 0
        }
      })

      if (!except) {
        this.lastActiveVideo = null
      }
    }
  }

  const init = (scope = document) => {
    const sections = scope.querySelectorAll('[data-stories-block]')

    sections.forEach((section) => {
      if (!section.__storiesContent) {
        section.__storiesContent = new StoriesContent(section)
      }
    })
  }

  document.addEventListener('DOMContentLoaded', () => init())
  document.addEventListener('shopify:section:load', (event) => init(event.target))
}
